import { message as AntMessage } from 'antd'

import i18n from 'i18n'
import { download, upload, downloadCheck, DownloadObj } from 'libs/jamUtil'
import jmapDownloadCache, {
  buildJmapDownloadCacheKey,
  trackObjectURL,
} from 'libs/CACHE/jmapDownloadCache'
import { generateFilename, convertHEIC } from 'utils'
import storageApiHelper from 'services/sharing-file'

// Type definitions
interface FileInfo {
  name: string
  size: number
  type: string
  blobId?: string | null
  isInline?: boolean
  cid?: string | null
  partId?: string | null
}

interface OriginFile extends File {
  blobId?: string | null
}

interface EMLFileData {
  filename: string
  content: ArrayBuffer
  mimeType: string
  disposition?: string
}

interface InnerDownloadOptions {
  blob: Blob | File
  name: string
  mimetype: string
  onProgress?: ((evt: any, progress: any) => void) | null
}

interface ReactObj {
  deleteAttachment?: (attachment: Attachment) => void
  notifyUploadProgress?: (id: string, progress: number | null, error?: Error | null) => void
}

interface ProgressCallback {
  resolve: (value: Attachment | PromiseLike<Attachment>) => void
  reject?: (reason?: unknown) => void
}

interface StorageUploadResponse {
  code: number
  data?: Array<{
    upload_id: string
    file_id?: string
    download_url?: string
    download_limit?: number
    download_count?: number
    expires_at?: string
  }>
  message?: string
}

export interface Attachment {
  id: string
  type: 'file' | 'draft' | 'eml'
  sendType: 'jmap' | 'sharingFile' | 'eml'
  file: FileInfo
  isUploaded: boolean
  draftFile?: FileInfo
  originFile?: OriginFile
  fileForEML?: EMLFileData
  isEML?: boolean
  xhr: XMLHttpRequest | null
  isLoading: boolean
  isDownloading?: boolean
  isUploading?: boolean
  waitingReq: ProgressCallback[]
  downloadObj?: DownloadObj | null
  progressCBList: Array<(evt: any, progress: any, error?: Error | null) => void>
  uploadWaitingReq: ProgressCallback[]
  isUploadProgressing: boolean
  uploadId?: string | null
  fileId?: string
  downloadUrl?: string
  downloadLimit?: number
  downloadCount?: number
  expiresAt?: string
  error?: Error | null
  download: (onProgress?: (evt: any, progress: any) => void) => Promise<DownloadObj | undefined>
  cancelProgress: () => void
  clearURL: () => void
  delete: () => void
  /**
   * download 완료 시 호출되는 listener 집합. Attachment 컴포넌트가 마운트 시점에
   * 자신의 re-render 콜백을 등록하고, helper.ts가 download then에서 모두 호출한다.
   * mutation으로 채워지는 `downloadObj`/`path`/`downloadPath`를 React가 감지하지 못하므로
   * 이 listener를 통해 `data-blob-url` attribute를 갱신.
   */
  downloadCompleteListeners?: Set<() => void>
  /** listener 등록(컴포넌트 mount 시 호출). 동일 콜백 중복 등록은 Set 시맨틱으로 무해. */
  addDownloadCompleteListener?: (listener: () => void) => void
  /** listener 해제(컴포넌트 unmount 시 호출). */
  removeDownloadCompleteListener?: (listener: () => void) => void
  compare: (other: Attachment) => boolean
  checkUpload: (
    progressCB?: (evt: any, progress: any, error?: Error | null) => void,
  ) => Promise<Attachment>
  checkUpload0?: () => Promise<Attachment>
}

const commonProps = (attachment: Attachment, reactObj?: ReactObj | null): Attachment => {
  const self = attachment

  attachment.xhr = null

  attachment.isLoading = false
  attachment.waitingReq = []

  const innerDownload = async (options: InnerDownloadOptions): Promise<DownloadObj> => {
    const ret = new DownloadObj()

    const { blob: fileBlob, name, mimetype, onProgress } = options

    let blob = fileBlob
    let fileName = name

    let downloadBlob: Blob = blob

    ret.file = {
      name,
      type: mimetype,
    }

    if (mimetype != null) {
      downloadBlob = new Blob([blob], { type: mimetype })
    }

    ret.downloadPath = trackObjectURL(window.URL.createObjectURL(downloadBlob))

    ret.saveFile = () => {
      console.log(' - downloadImg : ', fileName, ret.downloadPath)
      const link = document.createElement('a')
      link.href = ret.downloadPath!
      link.download = generateFilename(fileName)
      link.click()
      link.remove() // IE 미지원
    }

    if (fileName != null) {
      if (mimetype.indexOf('text/') === 0) {
        ret.text = await blob.text()
      }
    }

    if (blob.type === 'text/xml' && fileName != null) {
      const lcFileName = fileName.toLowerCase()
      if (lcFileName.endsWith('.svg') === true) {
        blob = new Blob([blob], { type: 'image/svg+xml' })
      } else if (lcFileName.endsWith('.heic') === true || lcFileName.endsWith('.heif') === true) {
        const tmpFile = new File([blob], fileName, { type: mimetype || 'image/heic' })
        // createObjectURL에는 blob, file 모두 사용가능하므로, file을 blob 대신 전달.
        if (onProgress != null) {
          onProgress(null, null)
        }
        const imgBlob: any = await convertHEIC(tmpFile, 'image/jpeg', 0.8)
        console.log(imgBlob)
        if (onProgress != null) {
          onProgress(null, null)
        }
        ret.blob = imgBlob as Blob
        ret.path = trackObjectURL(window.URL.createObjectURL(imgBlob as Blob))
        return ret
      } else if (mimetype != null) {
        blob = new Blob([blob], { type: mimetype })
      }
    } else if (mimetype != null) {
      blob = new Blob([blob], { type: mimetype })
    } else {
      blob = new Blob([blob], { type: 'application/octet-stream' })
    }

    ret.blob = blob
    ret.path = trackObjectURL(window.URL.createObjectURL(blob))

    return ret
  }

  const download0 = async (onProgress?: (evt: any, progress: any) => void): Promise<any> => {
    if (attachment.type === 'eml') {
      const { filename, content, mimeType } = attachment.fileForEML!
      const blob = new File([content], filename, { type: mimeType })
      attachment.originFile = blob as OriginFile
      return innerDownload({
        blob,
        name: filename,
        mimetype: mimeType,
        onProgress,
      })
    } else if (attachment.originFile != null && attachment.originFile.blobId == null) {
      return innerDownload({
        blob: attachment.originFile,
        name: attachment.originFile.name,
        mimetype: attachment.originFile.type,
        onProgress,
      })
    }

    const file = attachment.draftFile || attachment.originFile
    const cacheKey = buildJmapDownloadCacheKey(file)
    const cached = await jmapDownloadCache.getData(cacheKey)
    if (cached != null && cached.data != null) {
      attachment.isDownloading = false
      return cached.data
    }

    return download(file, true, true, onProgress, (xhr) => {
      attachment.xhr = xhr
      attachment.isDownloading = true
    }).then(
      (ret) => {
        attachment.xhr = null
        attachment.isDownloading = false
        if (ret != null) {
          jmapDownloadCache.putData(cacheKey, ret)
        }
        return ret
      },
      (err) => {
        console.log(' - download error - ', err)
        attachment.xhr = null
        attachment.isDownloading = false
        // throw err

        if (err.type === 'abort') {
          AntMessage.info(i18n.t('email.desc.downloadCanceled'))
        }
      },
    )
  }

  // download 완료 listener 슬롯. Attachment 컴포넌트가 mount/unmount 시 등록·해제.
  // mutation으로 채워지는 downloadObj/path/downloadPath를 React가 자동 감지 못하므로
  // listener를 통해 컴포넌트가 forceUpdate → data-blob-url attribute 갱신.
  attachment.downloadCompleteListeners = new Set()

  attachment.addDownloadCompleteListener = (listener: () => void) => {
    attachment.downloadCompleteListeners?.add(listener)
  }

  attachment.removeDownloadCompleteListener = (listener: () => void) => {
    attachment.downloadCompleteListeners?.delete(listener)
  }

  // listener 호출은 RAF 1 frame 디바운스 — React commit 후 outerHTML에 attribute가 박혀
  // 그 다음 sweep에서 보호 신호로 잡힌다. RAF 미지원 환경은 setTimeout(16) fallback.
  const notifyDownloadComplete = (): void => {
    const listeners = attachment.downloadCompleteListeners
    if (listeners == null || listeners.size === 0) return
    const fire = (): void => {
      listeners.forEach((listener) => {
        try {
          listener()
        } catch (e) {
          console.warn('Attachment downloadCompleteListener threw:', e)
        }
      })
    }
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(fire)
    } else {
      setTimeout(fire, 16)
    }
  }

  attachment.download = async (
    onProgress?: (evt: any, progress: any) => void,
  ): Promise<DownloadObj | undefined> => {
    if (attachment.downloadObj != null) {
      return attachment.downloadObj
    }

    if (attachment.isLoading) {
      return new Promise<DownloadObj | undefined>((resolve, reject) => {
        attachment.waitingReq.push({
          resolve: (() => {
            resolve(attachment.downloadObj)
          }) as ProgressCallback['resolve'],
          reject,
        })
      })
    }

    attachment.isLoading = true
    return new Promise<DownloadObj | undefined>((resolve, reject) => {
      download0(onProgress)
        .then((ret) => {
          attachment.downloadObj = ret
          attachment.waitingReq.forEach((promise) => {
            promise.resolve(attachment)
          })
          resolve(attachment.downloadObj)
          notifyDownloadComplete()
        })
        .catch((err) => {
          attachment.waitingReq.forEach((promise) => {
            promise.reject?.(err)
          })
          reject(err)
        })
        .finally(() => {
          attachment.waitingReq = []
          attachment.isLoading = false
        })
    })
  }

  attachment.cancelProgress = () => {
    if (attachment.xhr != null && attachment.downloadObj == null) {
      try {
        attachment.xhr.abort()
      } catch (err) {
        console.log(err)
      }
    }
    attachment.xhr = null
  }

  attachment.clearURL = () => {
    // path / downloadPath의 revoke는 jmapDownloadCache의 sweep이 단일 책임으로 담당한다.
    // (createObjectURL 시점에 trackObjectURL로 objectURLMap에 등록되어, 어느 outerHTML에도
    // 보이지 않는 시점에 sweep이 일괄 처리.) 여기서는 진행 중 다운로드만 abort.
    if (attachment.downloadObj == null) {
      attachment.cancelProgress()
    }
  }

  attachment.delete = () => {
    if (reactObj != null && reactObj.deleteAttachment != null) {
      reactObj.deleteAttachment(attachment)
    }

    if (attachment.sendType === 'sharingFile') {
      if (attachment.isUploading === true && attachment.uploadId != null) {
        storageApiHelper.cancelUpload(attachment.uploadId)
      } else if (attachment.isUploaded === true && attachment.fileId != null) {
        storageApiHelper.deleteFile(attachment.fileId)
      }
    }

    attachment.clearURL()
  }

  attachment.compare = (other: Attachment): boolean => {
    if (attachment === other) {
      return true
    } else if (
      attachment.file.blobId !== other.file.blobId ||
      attachment.file.name !== other.file.name ||
      attachment.file.size !== other.file.size ||
      attachment.file.type !== other.file.type
    ) {
      return false
    }
    return false
  }

  return self
}

const getAttachmentId = (file: File, cid?: string | null): string => {
  const curTime = new Date().getTime()
  return `${curTime}-${file.name}-${file.size}-${file.type}-${cid || ''}`
}

const createAttachment = (
  originFile: OriginFile,
  cid?: string | null,
  reactObj?: ReactObj | null,
  isEML: boolean = false,
): Attachment => {
  const id = getAttachmentId(originFile, cid)
  const attachment: Attachment = {
    id,
    type: 'file',
    sendType: 'jmap',
    file: {
      name: originFile.name,
      size: originFile.size,
      type: originFile.type,
      isInline: cid != null,
      cid,
    },
    isUploaded: originFile.blobId != null,
    progressCBList: [],
    uploadWaitingReq: [],
    isUploadProgressing: false,
    originFile,
    xhr: null,
    isLoading: false,
    waitingReq: [],
    download: async () => undefined,
    cancelProgress: () => {},
    clearURL: () => {},
    delete: () => {},
    compare: () => false,
    checkUpload: async (progressCB?: (evt: any, progress: any) => void): Promise<Attachment> => {
      if (attachment.isUploaded === true) {
        return attachment
      }

      if (progressCB != null) {
        attachment.progressCBList.push(progressCB)
      }

      if (attachment.isUploadProgressing !== true) {
        attachment.isUploadProgressing = true
        await attachment.checkUpload0!()
        attachment.uploadWaitingReq.forEach((promise) => {
          promise.resolve(attachment)
        })
        attachment.uploadWaitingReq = []
        attachment.progressCBList = []
        attachment.isUploadProgressing = false
      } else {
        await new Promise<void>((resolve) => {
          attachment.uploadWaitingReq.push({
            resolve: (() => resolve()) as ProgressCallback['resolve'],
          })
        })
      }

      return attachment
    },
    checkUpload0: (): Promise<Attachment> => {
      const onProgress = (evt: any, progress: any) => {
        console.log('--- onProgress : ', evt, progress)
        attachment.progressCBList.forEach((progressCB) => {
          if (progressCB != null) {
            progressCB(evt, progress)
          }
        })
        if (reactObj != null && reactObj.notifyUploadProgress != null) {
          reactObj.notifyUploadProgress(id, progress)
        }
      }
      return new Promise((resolve /* , reject */) => {
        const uploadFile = () => {
          upload(attachment.originFile!, onProgress, (xhr) => {
            attachment.xhr = xhr
            attachment.isUploading = true
          })
            .then((ret: any) => {
              attachment.file = {
                ...(ret as any),
                name: attachment.originFile!.name,
                size: attachment.originFile!.size,
                type: attachment.originFile!.type,
                isInline: cid != null,
                cid,
              }
              attachment.isUploaded = true
              attachment.xhr = null
              attachment.isUploading = false
              resolve(attachment)
            })
            .catch((err) => {
              console.log(' - upload error - ', err)
              attachment.xhr = null
              attachment.isUploading = false
              attachment.error = err
              // reject(err)

              if (err.type === 'abort') {
                AntMessage.info(i18n.t('email.desc.uploadCanceled'))
              } else if (reactObj != null && reactObj.notifyUploadProgress != null) {
                console.log(' --- notifyUploadProgress : ', err)
                reactObj.notifyUploadProgress(id, null, err)
              }
            })
        }

        if (attachment.file.blobId == null) {
          uploadFile()
        } else {
          downloadCheck(attachment.file).then((ret) => {
            console.log('download result - ', ret)
            if (ret === false) {
              uploadFile()
            } else {
              resolve(attachment)
            }
          })
        }
      })
    },
  }

  if (isEML === true) {
    attachment.type = 'eml'
    attachment.sendType = 'eml'
    attachment.isEML = true
    attachment.fileForEML = originFile as unknown as EMLFileData
    attachment.isUploaded = false

    const fileData = originFile as unknown as EMLFileData
    if (fileData.filename != null && fileData.filename.length > 0) {
      attachment.file.name = fileData.filename
    }
    if (fileData.mimeType != null && fileData.mimeType.length > 0) {
      attachment.file.type = fileData.mimeType
    }
    if (fileData.content != null && fileData.content.byteLength != null) {
      attachment.file.size = fileData.content.byteLength
    }
  }

  commonProps(attachment, reactObj)

  return attachment
}

const createAttachmentFromDraft = (draftFile: FileInfo, reactObj?: ReactObj | null): Attachment => {
  const isInline = draftFile.isInline != null ? draftFile.isInline : draftFile.cid != null

  const attachment: Attachment = {
    id: getAttachmentId(draftFile as any, draftFile.cid),
    type: 'draft',
    sendType: 'jmap',
    file: {
      ...draftFile,
      isInline,
    },
    draftFile,
    isUploaded: true,
    xhr: null,
    isLoading: false,
    waitingReq: [],
    progressCBList: [],
    uploadWaitingReq: [],
    isUploadProgressing: false,
    download: async () => undefined,
    cancelProgress: () => {},
    clearURL: () => {},
    delete: () => {},
    compare: () => false,
    checkUpload: (): Promise<Attachment> => {
      return new Promise((resolve, reject) => {
        if (attachment.downloadObj != null) {
          resolve(attachment)
        } else {
          downloadCheck(attachment.file).then((ret) => {
            console.log('download result - ', ret)
            if (ret === false) {
              reject('file not exist.')
            } else {
              resolve(attachment)
            }
          })
        }
      })
    },
  }

  commonProps(attachment, reactObj)

  return attachment
}

const createAttachmentForEML = (
  originFile: EMLFileData,
  cid?: string | null,
  reactObj?: ReactObj | null,
): Attachment => {
  const { filename, content, mimeType /* , disposition */ } = originFile

  const attachment: Attachment = {
    id: getAttachmentId({ name: filename, size: content.byteLength, type: mimeType } as any, cid),
    type: 'eml',
    sendType: 'eml',
    file: {
      name: filename,
      size: content.byteLength,
      type: mimeType,
      isInline: cid != null, // disposition !== 'attachment'
      cid,
    },
    isUploaded: true,
    isEML: true,
    originFile: originFile as any,
    fileForEML: originFile,
    xhr: null,
    isLoading: false,
    waitingReq: [],
    progressCBList: [],
    uploadWaitingReq: [],
    isUploadProgressing: false,
    download: async () => undefined,
    cancelProgress: () => {},
    clearURL: () => {},
    delete: () => {},
    compare: () => false,
    checkUpload: (): Promise<Attachment> => {
      return Promise.resolve(attachment)
    },
  }

  commonProps(attachment, reactObj)

  return attachment
}

// send 또는 save 전에만 존재할 수 있으므로, 항상 originFile이 존재함.
const createAttachmentForSharing = (
  originFile: OriginFile,
  cid?: string | null,
  reactObj?: ReactObj | null,
): Attachment => {
  const id = getAttachmentId(originFile, cid)
  const attachment: Attachment = {
    id,
    type: 'file',
    sendType: 'sharingFile',
    file: {
      name: originFile.name.normalize('NFC'),
      size: originFile.size,
      type: originFile.type,
      isInline: cid != null,
      cid,
    },
    isUploaded: false,
    uploadId: null,
    progressCBList: [],
    uploadWaitingReq: [],
    isUploadProgressing: false,
    originFile,
    error: null,
    xhr: null,
    isLoading: false,
    waitingReq: [],
    download: async () => undefined,
    cancelProgress: () => {},
    clearURL: () => {},
    delete: () => {},
    compare: () => false,
    checkUpload: async (
      progressCB?: (evt: any, progress: any, error?: Error | null) => void,
    ): Promise<Attachment> => {
      if (attachment.isUploaded === true) {
        return attachment
      }

      if (progressCB != null) {
        attachment.progressCBList.push(progressCB)
      }

      if (attachment.isUploadProgressing !== true) {
        attachment.isUploadProgressing = true
        let prepareResult: StorageUploadResponse
        try {
          prepareResult = await storageApiHelper.prepareUpload([attachment.file])
        } catch (err) {
          attachment.error =
            err instanceof Error ? err : new Error(i18n.t('editor.error.serverError'))
          if (reactObj != null && reactObj.notifyUploadProgress != null) {
            reactObj.notifyUploadProgress(id, null, attachment.error)
          }
          attachment.uploadWaitingReq.forEach((promise) => {
            promise.resolve(attachment)
          })
          attachment.uploadWaitingReq = []
          attachment.progressCBList = []
          attachment.isUploadProgressing = false
          return attachment
        }

        const { code, data, message } = prepareResult
        if (code !== 200) {
          let errorInfo = null
          switch (code) {
            case 415:
              {
                // InvalidFileType
                const sizeCheckStr = 'Each file must not exceed'
                const pos = message.indexOf(sizeCheckStr)
                if (pos >= 0) {
                  let limitStr = message.substring(pos + sizeCheckStr.length)
                  limitStr = limitStr.trim()
                  errorInfo = {
                    key: 'editor.error.sizeLimitExceededShort',
                    params: { limit: limitStr },
                  }
                } else {
                  // "No valid files to upload"
                  errorInfo = {
                    key: 'editor.error.invalidFileType',
                  }
                }
              }
              break
            case 507: // NotEnoughSpace
              errorInfo = {
                key: 'editor.error.notEnoughSpace',
              }
              break
            default:
              errorInfo = {
                key: 'editor.error.serverError',
              }
              break
          }

          /* i18next-extract-disable-next-line */
          const errMessage = i18n.t(errorInfo.key, errorInfo.params) as string
          attachment.error = new Error(errMessage)
          ;(attachment.error as any).i18nInfo = errorInfo

          if (reactObj != null && reactObj.notifyUploadProgress != null) {
            reactObj.notifyUploadProgress(id, null, attachment.error)
          }
        } else if (data != null && data.length > 0) {
          const { upload_id } = data[0]
          attachment.uploadId = upload_id
          await attachment.checkUpload0!()
        }
        attachment.uploadWaitingReq.forEach((promise) => {
          promise.resolve(attachment)
        })
        attachment.uploadWaitingReq = []
        attachment.progressCBList = []
        attachment.isUploadProgressing = false
      } else {
        await new Promise<void>((resolve) => {
          attachment.uploadWaitingReq.push({
            resolve: (() => resolve()) as ProgressCallback['resolve'],
          })
        })
      }

      return attachment
    },
    checkUpload0: (): Promise<Attachment> => {
      const onProgress = (evt: any, progress: any, error?: Error | null) => {
        console.log('--- onProgress : ', evt, progress)
        attachment.progressCBList.forEach((progressCB) => {
          if (progressCB != null) {
            progressCB(evt, progress, error)
          }
        })
        if (reactObj != null && reactObj.notifyUploadProgress != null) {
          reactObj.notifyUploadProgress(id, progress, error)
        }
      }
      return new Promise((resolve /* , reject */) => {
        const uploadFile = () => {
          storageApiHelper
            .upload(
              [{ file: attachment.originFile!, upload_id: attachment.uploadId! }],
              onProgress,
              (xhr) => {
                attachment.xhr = xhr
                attachment.isUploading = true
              },
            )
            .then((ret: StorageUploadResponse) => {
              const { code, data } = ret
              if (code === 200 && data != null && data.length > 0) {
                const { file_id, download_url, download_limit, download_count, expires_at } =
                  data[0]
                attachment.fileId = file_id
                attachment.downloadUrl = download_url
                attachment.downloadLimit = download_limit
                attachment.downloadCount = download_count
                attachment.expiresAt = expires_at
              } else {
                attachment.error = new Error(i18n.t('editor.error.serverError'))
              }

              attachment.file = {
                name: attachment.originFile!.name.normalize('NFC'),
                size: attachment.originFile!.size,
                type: attachment.originFile!.type,
                isInline: cid != null,
                cid,
              }
              attachment.isUploaded = true
              attachment.uploadId = null
              attachment.xhr = null
              attachment.isUploading = false
              resolve(attachment)
            })
            .catch((err) => {
              console.log(' - upload error - ', err)
              attachment.xhr = null
              attachment.isUploading = false
              attachment.error = err
              // reject(err)

              if (err.type === 'abort') {
                AntMessage.info(i18n.t('email.desc.uploadCanceled'))
              }
            })
        }

        uploadFile()
      })
    },
  }

  commonProps(attachment, reactObj)

  return attachment
}

export function getPreviewType(file: Pick<FileInfo, 'name' | 'type'>): number {
  let viewType = 0

  const { name, type } = file
  if (type != null) {
    const mimetype = type.toLowerCase()

    if (mimetype != null) {
      if (mimetype.indexOf('image/') === 0) {
        viewType = 1
      } else if (mimetype.indexOf('video/') === 0 || mimetype.indexOf('audio') === 0) {
        viewType = 2
      } else if (mimetype.indexOf('text/') === 0 && mimetype.indexOf('text/html') < 0) {
        viewType = 3
      } else if (mimetype === 'message/rfc822') {
        viewType = 4
      }
    }

    if (name != null && (viewType === 0 || viewType === 3)) {
      const idx = name.lastIndexOf('.')
      if (idx > 0) {
        const ext = name.substring(idx + 1).toLowerCase()
        switch (ext) {
          case 'png':
          case 'jpg':
          case 'jpeg':
            viewType = 1
            break
          case 'mp4':
          case 'avi':
          case 'mov':
          case 'wmv':
          case 'flv':
          case 'webm':
            viewType = 2
            break
          case 'txt':
            viewType = 3
            break
          case 'eml':
            viewType = 4
            break
          case 'pdf':
          case 'doc':
          case 'docx':
          case 'ppt':
          case 'pptx':
          case 'xls':
          case 'xlsx':
          case 'hwp':
          case 'hwpx':
          case 'odt':
            // case 'md':
            // case 'markdown':
            // case 'json':
            // case 'csv':
            // case 'xml':
            // case 'log':
            viewType = 5
            break
          default:
        }
      }
    }
  }

  return viewType
}

function isPreviewEnable(file: Pick<FileInfo, 'name' | 'type'>): boolean {
  console.log('isPreviewEnable - ', file)

  if (file != null && file.type != null) {
    const viewType = getPreviewType(file)
    return viewType > 0
  }

  return false
}

const helper = {
  createAttachment,
  createAttachmentFromDraft,
  createAttachmentForEML,
  createAttachmentForSharing,
  isPreviewEnable,
}

export default helper
